/* eslint-disable no-extra-parens */
/* eslint-disable react/display-name */
import PropTypes from 'prop-types';
import React, { memo, useEffect, useMemo, useRef } from 'react';

import { reduxConnect } from '../../common';
import { getCcpaCookie, getStatePrivacyPolicyCookie } from '../../common/utilities/ccpaHelper';

const storyPopupId = 'story';
const positionOfLastTaboolaWidgetOnPage = 'below_content';

const getTaboolaPageConfig = (position, taboolaPageId, taboolaInfo) => {
    if (!position && !taboolaPageId && !taboolaInfo?.length) {
        return;
    }
    
    return taboolaInfo?.find(x => x?.Position?.toLowerCase() === position?.toLowerCase() && x?.PageId?.toLowerCase() === taboolaPageId?.toLowerCase());
};

const mapStoreStateToProps = (storeState) => {
    const appState = storeState?.appState?.appState || {};
    const { enableTaboola, pageId, popupId, sourceProviderId, taboolaInfo, pageMetaData, user } = appState;
    
    const canonicalUrl = pageMetaData?.Canonical;

    const currentPageId = storeState?.taboola?.currentPage;
    const { complianceRequirements } = storeState?.legal || {};
    const { dataPrivacyEnabled, sensitiveDataRestrictionEnabled } = complianceRequirements || {};
    const currentLocationSlug = user?.ActiveLocation?.Locality?.SlugName;

    return {
        taboolaInfo,
        pageId,
        popupId,
        isEnabled: enableTaboola,
        sourceProviderId,
        complianceRequirements,
        canonicalUrl,
        currentPageId,
        dataPrivacyEnabled,
        sensitiveDataRestrictionEnabled,
        currentLocationSlug
    };
};

const mapDispatchToProps = {};

export const TaboolaFeedWidget = reduxConnect(mapStoreStateToProps, mapDispatchToProps) (memo(( {
    sourceProviderId,
    feedCategoryType = 'auto',
    height,
    width,
    isEnabled,
    canonicalUrl,
    pageId,
    popupId,
    complianceRequirements,
    dataPrivacyEnabled,
    position,
    taboolaInfo,
    sensitiveDataRestrictionEnabled,
    currentLocationSlug }) => { 

    const taboolaPageId = popupId?.toLowerCase() === storyPopupId ? popupId : pageId;

    const taboolaPageConfig = useMemo(() => {
        return getTaboolaPageConfig(position, taboolaPageId, taboolaInfo);
    }, [taboolaPageId, position, taboolaInfo]);

    const { ContainerId: containerId, 
        PlacementId: placementId, 
        Feed: feed, 
        Mode: mode, 
        TargetType: targetType, 
        Position: taboolaWidgetPosition } = taboolaPageConfig || {};
    
    const taboolaFeedDisplayed = useRef(false);
    const prevLocationSlug = useRef(null);
    const currentlyLoadedTaboolaContainerId = useRef();
    const taboolaRef = useRef(null);
    let divId = containerId;

    if (!isEnabled) {
        divId = `taboola_disabled_${(new Date).getTime()}`;
    }

    const displayTaboolaFeedWidget = () => {
        const ccpaCookie = getCcpaCookie(); // Still in use, since there can be users who'd previouly opted out of CCPA before we deploy new state Privacy
        const statePrivacyCookie = getStatePrivacyPolicyCookie();
        let userOptedOut = 'none';

        if (dataPrivacyEnabled || sensitiveDataRestrictionEnabled) {
            if (ccpaCookie?.doNotSell || statePrivacyCookie?.doNotSell || statePrivacyCookie?.limitUseOfPI) {
                userOptedOut = 'true';
            } else {
                userOptedOut = 'false';
            }
        }

        if (isEnabled
            && containerId
            && placementId
            && complianceRequirements
        ) {
            const placement = sourceProviderId === 'dt' ? `${placementId} - ${sourceProviderId}` : placementId;
            const pageDetail = { url: canonicalUrl };
            pageDetail[feed] = feedCategoryType;
            taboolaFeedDisplayed.current = true;

            window._taboola = window._taboola || [];
            window._taboola.push({
                mode: mode,
                container: containerId,
                placement: placement,
                target_type: targetType,
                cdns: userOptedOut
            });
            window._taboola.push(pageDetail);
            currentlyLoadedTaboolaContainerId.current = containerId;

            // The flush flag instructs Taboola to fetch the requested placements (and not wait for additional data).
            // Putting it below the last possible taboola feed on page as we need to pass the flush flag once only - after passing all placements for that page.
            window._taboola.push({ flush: true });
        }
    };

    useEffect(() => {
        if (isEnabled 
            && containerId 
            && mode
            && targetType 
            && placementId 
            && taboolaPageId
            && currentLocationSlug
            && ((containerId !== currentlyLoadedTaboolaContainerId.current) 
                || (currentLocationSlug !== prevLocationSlug.current))) {
            if (location.pathname.startsWith('/weather-videos')) {
                const videoPageTaboolaTime = setTimeout(() => {
                    displayTaboolaFeedWidget();
                    clearTimeout(videoPageTaboolaTime);
                }, 500);
                
            } else {
                displayTaboolaFeedWidget();
            }
        }

        prevLocationSlug.current = currentLocationSlug;
        
    }, [taboolaPageId, containerId, placementId, isEnabled, mode, targetType, currentLocationSlug]);

    if ((!position || !taboolaPageId || !taboolaInfo?.length)) {
        return null;
    }
        
    return <div id={divId}
        width={width}
        height={height}
        ref={taboolaRef}
    />;
    
}));

TaboolaFeedWidget.displayName = 'TaboolaFeedWidget';
TaboolaFeedWidget.propTypes = {
    pageId: PropTypes.string,
    sourceProviderId: PropTypes.string,
    feedCategoryType: PropTypes.string,
    height: PropTypes.number,
    width: PropTypes.number,
    isEnabled: PropTypes.bool,
    canonicalUrl: PropTypes.string,
    position: PropTypes.string,
    taboolaInfo: PropTypes.arrayOf({
        PageId: PropTypes.string,
        ContainerId: PropTypes.string,
        PlacementId: PropTypes.string,
        Feed: PropTypes.string,
        Mode: PropTypes.string,
        Position: PropTypes.string,
        TargetType: PropTypes.string
    }),
    popupId: PropTypes.string,
    complianceRequirements: PropTypes.object,
    currentPageId: PropTypes.string,
    setCurrentPage: PropTypes.func,
    dataPrivacyEnabled: PropTypes.bool,
    sensitiveDataRestrictionEnabled: PropTypes.bool
};
